<template>
  <div class="ak-cards" :class="rootClasses">
    <slot />
  </div>
</template>

<script>
import { computed, defineComponent } from 'vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-cards>
   *  <ak-cards-item>
   *     <ak-card>
   *       <ak-card-body>
   *         <p>Lorem ipsum dolor.</p>
   *       </ak-card-body>
   *     </ak-card>
   *   </ak-cards-item>
   * </ak-cards>
   */
  name: 'AkCards',

  provide() {
    return {
      akCards: this,
    }
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Mixed}
     * @description This will serve as the model value when using the
     * selectable cards options.
     */
    modelValue: {
      type: [String, Number, Boolean, Object, Array, Function],
      default: undefined,
    },

    /**
     * @type {String}
     * @description Determines if the cards will an adjusted gutter size
     */
    gutter: {
      type: String,
      validator: (v) => ['tiny', 'small'].indexOf(v) >= 0,
      default: null,
    },

    centered: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Determines if the cards are disabled or not.
     */
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  /**
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: ['update:modelValue'],

  /**
   * @param {Object} props - The properties of the component
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit }) {
    const rootClasses = computed(() => {
      return {
        [`ak-cards--${props.gutter}`]: props.gutter,
        'is-disabled': props.disabled,
        'ak-cards--no-wrap': props.noWrap,
        'ak-cards--centered': props.centered,
      }
    })

    const select = (value) => {
      if (!props.disabled) {
        emit('update:modelValue', value)
      }
    }

    return {
      rootClasses,
      select,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
.ak-cards {
  margin-left: -$spacing;
  margin-top: -$spacing;
  clear: both;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  flex-wrap: wrap;

  > * {
    padding-left: $spacing;
    padding-top: $spacing;
  }

  &--small {
    margin-top: -#{halve($spacing)};
    margin-left: -#{halve($spacing)};
    > * {
      padding-left: halve($spacing);
      padding-top: halve($spacing);
    }
  }

  &--tiny {
    margin-top: -#{quarter($spacing)};
    margin-left: -#{quarter($spacing)};
    > * {
      padding-left: quarter($spacing);
      padding-top: quarter($spacing);
    }
  }

  &--centered {
    -webkit-justify-content: center;
    -ms-flex-pack: center;
    justify-content: center;
  }
}
</style>
